/**
 * PURPOSE: Pantalla de inicio de sesión de Winscore.
 * RESPONSIBILITIES: Autenticar usuario y redirigir según rol, is_one_company y compañía principal/habilitada.
 * COLLABORATORS: src/features/authSlice.js, src/utils/authUser.js, src/pages/Companies.jsx, src/sections/CampeonatosSection.jsx
 * ROUTE: src/components/Login/Login.jsx
 */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { LoginUser, reset } from "../../features/authSlice.js";
import {
  buildCampeonatosUrlByCompany,
  getUserCompanyId,
  getUserRole,
  isOneCompanyUser,
} from "../../utils/authUser.js";

const HAYLLIS_LOGO_URL =
  "https://resultados.hayllis.com/assets/logos/hayllis.png";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user, isError, isSuccess, isLoading, message } = useSelector(
    (state) => state.auth
  );

  useEffect(() => {
    if (!(user || isSuccess)) return;

    try {
      if (!user || typeof user !== "object") {
        console.log("Login redirect: no llegó un user válido.", user);
        navigate("/companies");
        return;
      }

      const role = getUserRole(user);
      const oneCompany = isOneCompanyUser(user);
      const companyId = getUserCompanyId(user);

      if (role === 1) {
        if (oneCompany) {
          if (companyId) {
            navigate(buildCampeonatosUrlByCompany(companyId));
          } else {
            console.log(
              "Login redirect: rol 1 one-company sin idcompany válido.",
              user
            );
            navigate("/companies");
          }
        } else {
          navigate("/companies");
        }
        return;
      }

      if (role === 2 || role === 3) {
        navigate(oneCompany ? "/campeonatos" : "/companies");
        return;
      }

      if (role === 4) {
        if (oneCompany) {
          if (companyId) {
            navigate(buildCampeonatosUrlByCompany(companyId));
          } else {
            console.log(
              "Login redirect: rol 4 one-company sin idcompany válido.",
              user
            );
            navigate("/campeonatos");
          }
        } else {
          navigate("/companies");
        }
        return;
      }

      navigate("/campeonatos");
    } catch (error) {
      console.log("Login redirect error:", error);
      navigate("/companies");
    } finally {
      dispatch(reset());
    }
  }, [user, isSuccess, dispatch, navigate]);

  const Auth = (e) => {
    e.preventDefault();
    dispatch(LoginUser({ username, password }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F7FCFD] via-[#EFFAFA] to-[#E8F7F4] text-[#00334D] md:h-screen md:overflow-hidden">
      <div className="flex min-h-screen w-full md:h-screen">
        {/* LADO IZQUIERDO - LOGIN */}
        <section className="relative z-10 flex min-h-screen w-full flex-col px-5 sm:px-8 md:h-screen md:w-1/2 md:px-10 lg:px-12">
          {/* Decoración suave */}
          <div className="pointer-events-none absolute left-0 top-0 -z-10 h-72 w-72 rounded-full bg-[#12D6C2]/20 blur-3xl" />
          <div className="pointer-events-none absolute bottom-0 right-0 -z-10 h-72 w-72 rounded-full bg-[#00D665]/10 blur-3xl" />

          {/* HEADER */}
          <header className="flex shrink-0 items-center justify-between py-5 md:py-6">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl border border-[#00577B]/10 bg-white shadow-[0_12px_30px_rgba(0,87,123,0.12)]">
                <img
                  src="/assets/logos/logo3.png"
                  alt="Winscore"
                  className="h-full w-full object-cover"
                />
              </div>

              <div className="leading-none">
                <p className="text-base font-black tracking-[-0.03em] text-[#003F5F]">
                  WINSCORE
                </p>
                <p className="mt-1 text-xs font-semibold tracking-[0.01em] text-[#00577B]/70">
                  Hayllis Software deportivo
                </p>
              </div>
            </div>
          </header>

          {/* MAIN */}
          <main className="flex flex-1 items-center justify-center py-4 md:py-2">
            <div className="w-full max-w-[430px] rounded-[28px] border border-white/80 bg-white/90 px-7 py-8 shadow-[0_24px_70px_rgba(0,63,95,0.13)] backdrop-blur-xl sm:px-9 md:py-8 lg:max-w-[450px]">
              <div className="mb-7">
                <div className="mb-5 h-1.5 w-14 rounded-full bg-gradient-to-r from-[#00577B] via-[#12D6C2] to-[#00D665]" />

                <h1 className="text-4xl font-black leading-[0.98] tracking-[-0.06em] text-[#00334D] sm:text-[2.55rem]">
                  Bienvenido a Winscore
                </h1>

                {/* <p className="mt-4 text-base font-medium leading-7 text-[#00577B]/75">
                  Accede al sistema de resultados, gestión deportiva y control
                  de competencias de Hayllis.
                </p> */}
              </div>

              <form onSubmit={Auth} className="space-y-5">
                {isError && (
                  <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-center text-sm font-bold text-red-700">
                    {message}
                  </div>
                )}

                <div>
                  <label
                    htmlFor="username"
                    className="mb-2 block text-sm font-black text-[#003F5F]"
                  >
                    Usuario <span className="text-red-500">*</span>
                  </label>

                  <input
                    id="username"
                    type="text"
                    name="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                    placeholder="Ingresa tu usuario"
                    className="h-12 w-full rounded-2xl border border-[#00577B]/20 bg-white px-4 text-base font-semibold text-[#00334D] shadow-[0_8px_24px_rgba(0,87,123,0.06)] outline-none transition placeholder:text-[#00577B]/55 hover:border-[#12D6C2]/70 focus:border-[#12D6C2] focus:ring-4 focus:ring-[#12D6C2]/15"
                  />
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="mb-2 block text-sm font-black text-[#003F5F]"
                  >
                    Contraseña <span className="text-red-500">*</span>
                  </label>

                  <input
                    id="password"
                    type="password"
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    placeholder="Ingresa tu contraseña"
                    className="h-12 w-full rounded-2xl border border-[#00577B]/20 bg-white px-4 text-base font-semibold text-[#00334D] shadow-[0_8px_24px_rgba(0,87,123,0.06)] outline-none transition placeholder:text-[#00577B]/55 hover:border-[#12D6C2]/70 focus:border-[#12D6C2] focus:ring-4 focus:ring-[#12D6C2]/15"
                  />
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-[#00577B]/75">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-[#00577B]/25 text-[#00577B] accent-[#00577B]"
                    />
                    Recordarme
                  </label>

                  <a
                    href="#replace-with-a-link"
                    className="text-sm font-black text-[#00577B] transition hover:text-[#12D6C2]"
                  >
                    ¿Olvidaste tu contraseña?
                  </a>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="h-13 mt-2 flex w-full items-center justify-center rounded-2xl bg-gradient-to-r from-[#00577B] via-[#12D6C2] to-[#00D665] px-5 py-3.5 text-base font-black text-white shadow-[0_16px_34px_rgba(0,87,123,0.28)] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_20px_42px_rgba(0,87,123,0.34)] active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0"
                >
                  {isLoading ? "Validando acceso..." : "Ingresar"}
                </button>
              </form>
            </div>
          </main>

          {/* FOOTER */}
          <footer className="shrink-0 py-4 text-center">
            <p className="text-xs font-bold text-[#003F5F]/55">
              © WINSCORE · Versión 2.0
            </p>
          </footer>
        </section>

        {/* LADO DERECHO - PORTADA HAYLLIS */}
        <aside className="relative hidden h-screen w-1/2 items-center justify-center overflow-hidden bg-gradient-to-br from-[#00334D] via-[#00577B] to-[#00D665] p-10 md:flex lg:p-14">
          {/* Decoraciones */}
          <div className="absolute -right-28 -top-28 h-96 w-96 rounded-full bg-white/15 blur-sm" />
          <div className="absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-white/10 blur-sm" />
          <div className="absolute right-20 top-20 h-32 w-32 rounded-full bg-[#12D6C2]/30 blur-2xl" />
          <div className="absolute bottom-24 left-20 h-40 w-40 rounded-full bg-[#00D665]/25 blur-2xl" />

          <div className="absolute inset-0 opacity-[0.14] [background-image:linear-gradient(rgba(255,255,255,.28)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.28)_1px,transparent_1px)] [background-size:42px_42px]" />

          <div className="relative z-10 w-full max-w-[560px] rounded-[34px] border border-white/30 bg-white/15 p-8 shadow-[0_34px_100px_rgba(0,28,44,0.34)] backdrop-blur-2xl lg:p-10">
            <div className="mb-7 flex items-center justify-center rounded-[28px] bg-white/90 p-6 shadow-[0_24px_70px_rgba(0,28,44,0.18)] lg:p-8">
              <img
                src={HAYLLIS_LOGO_URL}
                alt="Hayllis Software deportivo"
                className="block h-auto w-full max-w-[430px]"
              />
            </div>

            <h2 className="text-[2.15rem] font-black leading-[1.02] tracking-[-0.06em] text-white lg:text-[2.65rem]">
              Resultados, gestión y competencia en vivo.
            </h2>

            <p className="mt-4 max-w-[490px] text-base font-medium leading-7 text-white/80 lg:text-lg">
              {/* Plataforma deportiva para administrar campeonatos, controlar
              partidos y visualizar resultados con una experiencia moderna. */}
              {/* Plataforma deportiva para organizar campeonatos, gestionar la competencia y presentar resultados en tiempo real. */}
              Plataforma deportiva para administrar campeonatos, gestionar competencias y visualizar resultados con una experiencia moderna.
            </p>

            <div className="mt-8 grid grid-cols-3 gap-3">
              <div className="rounded-2xl border border-white/20 bg-white/15 px-3 py-3 text-center text-sm font-black text-white">
                Resultados
              </div>
              <div className="rounded-2xl border border-white/20 bg-white/15 px-3 py-3 text-center text-sm font-black text-white">
                Planillas
              </div>
              <div className="rounded-2xl border border-white/20 bg-white/15 px-3 py-3 text-center text-sm font-black text-white">
                Eventos
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Login;